import { useState, useRef, useEffect } from "react";

// A warm, handmade classic Mason tip jar that fills with a growing pile of
// copper pennies as the balance rises. Rounded shoulders, embossed rings, a
// two-piece screw-band lid, a taped paper label. Jiggles + drops a penny
// whenever a donation lands.
const BODY = "M70,66 L130,66 L130,72 C148,76 156,88 156,104 L156,214 C156,230 148,236 134,236 L66,236 C52,236 44,230 44,214 L44,104 C44,88 52,76 70,72 Z";
const INNER = "M72,72 L128,72 C146,78 150,90 150,106 L150,210 C150,224 144,230 132,230 L68,230 C56,230 50,224 50,210 L50,106 C50,90 54,78 72,72 Z";

const TOP_Y = 84;   // interior top (just under the shoulder)
const BOT_Y = 226;  // interior bottom

// Fixed field of penny positions covering the whole interior, bottom-first.
const ALL_COINS = (() => {
  const arr = [];
  let row = 0;
  for (let y = BOT_Y - 4; y >= TOP_Y; y -= 8.6) {
    const off = row % 2 ? 11 : 0;
    for (let x = 56 + off; x <= 144; x += 21) {
      // a little deterministic wobble so the pile looks hand-poured
      const jx = ((Math.sin(x * 12.9 + row * 7.3) * 43758.5) % 1) * 3 - 1.5;
      const jy = ((Math.sin(x * 4.1 + row * 2.7) * 1269.1) % 1) * 2.4 - 1.2;
      arr.push({ x: x + jx, y: y + jy, k: `${row}-${x}` });
    }
    row++;
  }
  return arr;
})();

export default function TipJar({ balance = 0, pct, className = "" }) {
  const NICE = [25, 50, 100, 250, 500, 1000, 2500, 5000, 10000, 25000, 50000, 100000];
  let fillPct;
  if (typeof pct === "number") {
    fillPct = Math.max(0, Math.min(100, pct));
  } else {
    const cap = NICE.find((n) => n > balance) || Math.ceil((balance || 1) * 1.25);
    fillPct = balance <= 0 ? 0 : Math.max(6, Math.min(94, (balance / cap) * 100));
  }
  const interiorH = BOT_Y - TOP_Y;
  const fillTop = BOT_Y - interiorH * (fillPct / 100);
  const coins = ALL_COINS.filter((c) => c.y >= fillTop - 4);

  const [drop, setDrop] = useState(0);
  const [jiggle, setJiggle] = useState(false);
  const prev = useRef(balance);
  useEffect(() => {
    if (balance > prev.current + 0.001) {
      setDrop((s) => s + 1);
      setJiggle(true);
      const t = setTimeout(() => setJiggle(false), 680);
      prev.current = balance;
      return () => clearTimeout(t);
    }
    prev.current = balance;
  }, [balance]);

  return (
    <svg viewBox="0 0 200 252" className={`${className} ${jiggle ? "jar-jiggle" : ""}`} role="img" aria-label="Community tip jar" data-testid="tip-jar-graphic">
      <defs>
        <linearGradient id="tjLid" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#E7A579" />
          <stop offset="100%" stopColor="#B96A3C" />
        </linearGradient>
        <linearGradient id="tjGlass" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#F3F8F4" />
          <stop offset="100%" stopColor="#E7EDE6" />
        </linearGradient>
        <radialGradient id="tjCoin" cx="0.35" cy="0.3" r="0.85">
          <stop offset="0%" stopColor="#F4C77C" />
          <stop offset="55%" stopColor="#D5922F" />
          <stop offset="100%" stopColor="#A9651F" />
        </radialGradient>
        <clipPath id="tjInner"><path d={INNER} /></clipPath>
      </defs>

      {/* shadow */}
      <ellipse cx="100" cy="244" rx="58" ry="7" fill="#6E4A5E" opacity="0.12" />

      {/* glass body */}
      <path d={BODY} fill="url(#tjGlass)" />

      {/* pile of pennies (clipped), grows with balance */}
      <g clipPath="url(#tjInner)">
        {coins.map((c) => (
          <g key={c.k}>
            <ellipse cx={c.x} cy={c.y} rx="10.5" ry="8.6" fill="url(#tjCoin)" stroke="#8C520F" strokeWidth="0.8" />
            <ellipse cx={c.x} cy={c.y} rx="6.8" ry="5.4" fill="none" stroke="#B9811F" strokeWidth="0.7" opacity="0.55" />
            <ellipse cx={c.x - 2.6} cy={c.y - 2.4} rx="2.2" ry="1.5" fill="#FBE7B2" opacity="0.6" />
          </g>
        ))}
      </g>

      {/* penny dropping in on each donation */}
      {drop > 0 && (
        <g key={`c${drop}`} className="jar-coin-fall" style={{ transformBox: "fill-box", transformOrigin: "center" }}>
          <ellipse cx="100" cy={Math.max(96, fillTop - 4)} rx="10" ry="9" fill="url(#tjCoin)" stroke="#8C520F" strokeWidth="1" />
          <ellipse cx="100" cy={Math.max(96, fillTop - 4)} rx="6.4" ry="5.2" fill="none" stroke="#B9811F" strokeWidth="0.7" opacity="0.55" />
        </g>
      )}

      {/* glass outline + embossed mason rings + highlight */}
      <path d={BODY} fill="none" stroke="#8C7A6B" strokeWidth="2.5" strokeLinejoin="round" />
      <path d="M50,98 Q100,104 150,98" fill="none" stroke="#ffffff" strokeWidth="2.5" opacity="0.4" />
      <path d="M50,108 Q100,114 150,108" fill="none" stroke="#ffffff" strokeWidth="2.5" opacity="0.3" />
      <path d="M58,92 Q56,160 66,224" fill="none" stroke="#ffffff" strokeWidth="5" strokeLinecap="round" opacity="0.4" />

      {/* two-piece screw-band lid */}
      <rect x="66" y="28" width="68" height="12" rx="4" fill="#CF8559" stroke="#8F5230" strokeWidth="2" />
      <rect x="58" y="38" width="84" height="24" rx="6" fill="url(#tjLid)" stroke="#8F5230" strokeWidth="2.5" />
      {[66, 76, 86, 96, 106, 116, 126].map((x) => (
        <line key={x} x1={x} y1="42" x2={x} y2="58" stroke="#9A5833" strokeWidth="1" opacity="0.5" />
      ))}
      {/* coin slot */}
      <rect x="88" y="31" width="24" height="3.6" rx="1.8" fill="#4A2E20" />

      {/* taped paper label */}
      <g transform="rotate(-3 100 166)">
        <rect x="48" y="140" width="104" height="52" rx="5" fill="#FFFDF7" stroke="#E7DCC7" strokeWidth="1.5" />
        <rect x="90" y="136" width="20" height="9" rx="1" fill="#EFE7D4" opacity="0.9" transform="rotate(-8 100 140)" />
        <text x="100" y="163" textAnchor="middle" fontFamily="'Fraunces', Georgia, serif" fontStyle="italic" fontSize="14" fill="#8A5A44">have a penny,</text>
        <text x="100" y="182" textAnchor="middle" fontFamily="'Fraunces', Georgia, serif" fontStyle="italic" fontSize="14" fill="#8A5A44">leave a penny</text>
      </g>
    </svg>
  );
}
